import { Formik, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { CommonLayout } from '../../commonComponents';
import { Box, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import UseRadioGroup from '../../../lib/formik/Radio';
import { RISK_CATEGORIZATION } from '../../../utils/constant';
import { MFTextField } from '../../../lib/formik';
import { LoadingButton } from '@mui/lab';
import { riskCategoryValidation } from '../../../utils/schema';
import { useHistory, useLocation, useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { getNctApplicationDetails, updateNctApplication } from '../../../redux-store/actions/nct';
import { useDispatch } from 'react-redux';
import { ApplicationNctProps } from '../../../redux-store/types/nct';

export type FormValuesType = {
  riskCategory: string;
  riskCategoryExplanation: string;
};

const RiskCategorizationStandalone = (): JSX.Element => {
  const formikRef = useRef<FormikProps<FormValuesType>>(null);
  const location = useLocation<{
    id: string;
    source: string;
    applicationNumber: string;
    applicationId?: string;
  }>();
  const { applicationNumber = '', applicationId = '' } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [application, setApplication] = useState<ApplicationNctProps>();
  const [riskData, setRiskData] = useState({
    riskCategory: '',
    riskCategoryExplanation: '',
  });

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        const _application = (await dispatch(
          getNctApplicationDetails(id ?? applicationId)
        )) as unknown as ApplicationNctProps;
        setApplication(_application);
        setRiskData({
          riskCategory: _application?.riskCategory,
          riskCategoryExplanation: _application?.riskCategoryExplanation,
        });
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    })();
    setLoading(true);
  }, [id, applicationId]);

  const onHandleSubmit = async (values: FormValuesType) => {
    // console.log(values);
    setUpdateLoading(true);
    try {
      await dispatch(
        updateNctApplication({
          body: { ...application, ...values },
          applicationId: id ?? applicationId,
          toastMessage: '',
        })
      );
      history.push('/risk-categorization');
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <CommonLayout>
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            fontWeight: 500,
            mb: { xs: 1, md: 1 },
            p: { xs: '5px 15px', md: '' },
            bgcolor: '#e5f1f8',
            borderRadius: '10px',
          }}>
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <IconButton
              sx={{ height: 'fit-content', p: 0 }}
              component={RouterLink}
              to={`/risk-categorization`}>
              <NavigateBeforeIcon fontSize="large" sx={{ color: 'common.black' }} />
            </IconButton>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 18,
                fontWeight: 500,
                color: 'primary.main',
              }}>
              Risk Categorization
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 18,
              fontWeight: 500,
              color: 'primary.main',
            }}>
            Application Number: {application?.applicationNumber}
          </Typography>
        </Box>
        {/* <SubHeading sx={{ marginBottom: '8px' }}>{'Risk Categorization'}</SubHeading> */}
        <Box
          sx={{
            bgcolor: 'white',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
            borderRadius: '10px',
            py: 5,
          }}>
          {loading ? (
            <Skeleton variant="rectangular" width="100%" height={200} />
          ) : (
            <Formik<FormValuesType>
              enableReinitialize
              innerRef={formikRef}
              validationSchema={riskCategoryValidation}
              initialValues={riskData}
              onSubmit={onHandleSubmit} // Not used directly
            >
              {({ handleSubmit, values, setValues, errors }) => (
                <Grid
                  container
                  rowSpacing={1}
                  sx={{
                    width: '80%',
                    mx: 'auto',
                    '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
                  }}
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}>
                  <Grid item xs={12} sm={12}>
                    <UseRadioGroup
                      formLabel="Risk Category *"
                      name={`riskCategory`}
                      // defaultValue="current"
                      items={Object.keys(RISK_CATEGORIZATION).map((riskCategory) => ({
                        label: RISK_CATEGORIZATION[riskCategory],
                        value: riskCategory,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <MFTextField
                      name={`riskCategoryExplanation`}
                      label={`Please provide a brief explanation for your selected risk category`}
                      placeholder="Type here"
                      multiline
                      rows={4}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <LoadingButton
                      sx={{
                        color: 'common.white',
                        minWidth: { xs: '100px', sm: '150px', md: '200px' },
                        mt: 4,
                        fontWeight: 600,
                        lineHeight: 1.1,
                        ml: 3,
                      }}
                      loadingPosition="start"
                      type="submit"
                      loading={updateLoading}
                      variant="contained">
                      Update
                    </LoadingButton>
                  </Grid>
                </Grid>
              )}
            </Formik>
          )}
        </Box>
      </>
    </CommonLayout>
  );
};

export default RiskCategorizationStandalone;
